import React from 'react';
import { Link } from 'react-router-dom';
import { createSlug } from '@/pages/SessionDetail';
import { Button } from '@/components/ui/button';

interface SessionItemProps {
  session: {
    time: string;
    type: string;
    title: string;
    speaker: string;
    description?: string | null;
    day?: string; // Add day for routing
  };
  index: number;
  animationDelayOffset?: number;
}

export const SessionItem = ({ session, index, animationDelayOffset = 0 }: SessionItemProps) => {
  const getBadgeColor = (type: string) => {
    switch (type.toLowerCase()) {
      case "keynote":
        return "bg-purple-600";
      case "talk":
        return "bg-blue-600";
      case "panel":
        return "bg-yellow-600";
      case "break":
        return "bg-gradient-to-r from-orange-500 to-orange-600";
      case "workshop":
        return "bg-green-600";
      case "closing":
        return "bg-red-600";
      case "session":
        return "bg-blue-600";
      default:
        return "bg-blue-600";
    }
  };
  
  // Function to format description with line breaks
  const formatDescription = (description: string | null | undefined) => {
    if (!description) return null;
    
    // Split by newline character and map each line
    return description.split('\n').map((line, i, arr) => (
      <span key={i}>
        {line}
        {i < arr.length - 1 && <br />}
      </span>
    ));
  };
  
  // Skip linking for breaks and similar non-session items
  const isLinkableSession = !session.type?.toLowerCase().includes('break');
  const isBreakSession = session.type?.toLowerCase().includes('break');
  
  // Create session content
  const sessionContent = (
    <>
      <div className="flex items-center gap-2 mb-1">
        <span className="text-purple-600 font-medium">{session.time}</span>
        <span className={`px-2 py-0.5 text-xs ${getBadgeColor(session.type)} text-white rounded`}>
          {session.type}
        </span>
      </div>
      <h4 className="text-black font-medium font-arial-black">{session.title}</h4>
      {session.speaker && <p className="text-gray-500 text-sm" style={{ fontSize: '0.9rem' }}>{session.speaker}</p>}
      {session.description && (
        <div className="text-gray-500 mt-2 text-sm">
          {formatDescription(session.description)}
        </div>
      )}
      {isBreakSession && (
        <div className="mt-3">
          <Link to="/register">
            <Button className="bg-green-500 hover:bg-green-600 text-white px-4 py-2 text-sm">
              Register Now
            </Button>
          </Link>
        </div>
      )}
    </>
  );
  
  return (
    <div className="animate-fade-in" style={{ animationDelay: `${(index * 150) + animationDelayOffset}ms` }}>
      {isLinkableSession && session.day ? (
        <Link 
          to={`/agenda/${session.day}/${createSlug(session.title)}`} 
          className="block hover:bg-gray-100 transition-colors p-2 -m-2 rounded-md"
        >
          {sessionContent}
        </Link>
      ) : (
        <div className="p-2 -m-2">
          {sessionContent}
        </div>
      )}
    </div>
  );
};
